import { callDelete, callGet, callPatch, callPost } from '@/api/fetcher';
import {
  CreateReportParams,
  MetricRange,
  RenameReportParams,
  Report,
  ReportMetric,
  ReportsListParams,
  ReportsListResponse,
} from '@/shared/types/reports';

export type UpdateReportBody = Partial<Pick<Report, 'name' | 'metrics'>>;

const DEFAULT_RANGE: MetricRange = { kind: 'relative', period: 'last_7_days' };

const normalizeRange = (raw: ReportMetric['range'] | undefined): MetricRange => {
  if (!raw) {
    return DEFAULT_RANGE;
  }
  // Legacy persisted shape: a bare string like "last_7_days".
  if (typeof raw === 'string') {
    return { kind: 'relative', period: raw };
  }
  return raw;
};

const normalizeMetric = (metric: ReportMetric): ReportMetric => ({
  ...metric,
  range: normalizeRange(metric.range),
});

const normalizeReport = (report: Report): Report => ({
  ...report,
  metrics: (report.metrics ?? []).map(normalizeMetric),
});

export const endpoints = Object.freeze({
  reports: '/www/api/v1/reports',
  report: (reportId: string) => `/www/api/v1/reports/${reportId}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/reports/check_name?${params}`;
  },
});

const buildQuery = (params: ReportsListParams = {}) => {
  const search = new URLSearchParams();
  if (params.name) {
    search.set('name', params.name);
  }
  if (params.cursor) {
    search.set('cursor', params.cursor);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  if (params.sort) {
    search.set('sort', params.sort);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export const API = Object.freeze({
  listReports: async (params?: ReportsListParams): Promise<ReportsListResponse> => {
    const response: ReportsListResponse = await callGet(
      `${endpoints.reports}${buildQuery(params)}`
    );
    return { ...response, reports: response.reports.map(normalizeReport) };
  },

  getReport: async (reportId: string): Promise<Report> => {
    const report: Report = await callGet(endpoints.report(reportId));
    return normalizeReport(report);
  },

  updateReport: async (reportId: string, body: UpdateReportBody): Promise<Report> => {
    const report: Report = await callPatch(endpoints.report(reportId), body);
    return normalizeReport(report);
  },

  createReport: async (body: CreateReportParams): Promise<Report> =>
    normalizeReport(await callPost(endpoints.reports, body)),

  renameReport: async (reportId: string, body: RenameReportParams): Promise<Report> =>
    normalizeReport(await callPatch(endpoints.report(reportId), body)),

  deleteReport: async (reportId: string): Promise<void> => callDelete(endpoints.report(reportId)),
});
